import { useEffect, useState, type FormEvent } from 'react'

import { DataTable, type Column } from '@/components/data/DataTable'
import { DetailPane, KeyValue, detailDangerClass } from '@/components/data/DetailPane'
import { FilterBar } from '@/components/data/FilterBar'
import { SplitView } from '@/components/data/SplitView'
import { StatusText } from '@/components/data/StatusText'
import { Page } from '@/components/layout/Page'
import { SettingsField, SettingsStatus, SettingsTabs, settingsPrimaryClass } from '@/components/SettingsTabs'
import { api } from '@/lib/api'
import { useSelection } from '@/lib/useSelection'

type TokenInfo = {
  token_type: string
  label: string
  created_at: string
  last_used_at: string | null
}

type Kind = 'jira_api_token' | 'org_admin_api_key'

type Row = { id: Kind; name: string; usedFor: string; stored: TokenInfo | null }

const KINDS: { id: Kind; name: string; usedFor: string }[] = [
  { id: 'jira_api_token', name: 'Jira API token', usedFor: 'Jira REST calls made as you' },
  { id: 'org_admin_api_key', name: 'Org-admin API key', usedFor: 'admin.atlassian.com: users, directories' },
]

const when = (iso: string | null) => (iso ? new Date(iso).toLocaleString() : '—')

const COLUMNS: Column<Row>[] = [
  { key: 'name', header: 'Credential', width: '24%' },
  { key: 'state', header: 'State', width: '120px', render: (r) => <StatusText status={r.stored ? 'stored' : 'not stored'}  /> },
  { key: 'label', header: 'Label', mono: true, render: (r) => r.stored?.label ?? '—', title: (r) => r.stored?.label },
  { key: 'created', header: 'Stored', mono: true, width: '190px', render: (r) => when(r.stored?.created_at ?? null) },
  { key: 'used', header: 'Last used', mono: true, width: '190px', render: (r) => when(r.stored?.last_used_at ?? null) },
]

function JiraForm({ stored, onDone }: { stored: boolean; onDone: (msg: string) => void }) {
  const [baseUrl, setBaseUrl] = useState('')
  const [token, setToken] = useState('')
  const [msg, setMsg] = useState<string | null>(null)
  async function submit(e: FormEvent) {
    e.preventDefault()
    setMsg(null)
    // Second line of defence, independent of autofill: the stored secret is never shown back,
    // so a wrong base_url would only surface far from here as broken Jira calls.
    if (!/^https:\/\/[^\s/]+/.test(baseUrl.trim())) {
      setMsg('Error: base_url must look like https://<tenant>.atlassian.net')
      return
    }
    try {
      await api('/tokens', {
        method: 'POST',
        body: JSON.stringify({ token_type: 'jira_api_token', base_url: baseUrl.trim(), api_token: token }),
      })
      setBaseUrl('')
      setToken('')
      onDone('Jira token stored.')
    } catch (err) {
      setMsg(`Error: ${(err as Error).message}`)
    }
  }
  return (
    <form onSubmit={submit} className="space-y-3">
      <SettingsField id="base" label="base_url" placeholder="https://<tenant>.atlassian.net" value={baseUrl} onChange={setBaseUrl} />
      <SettingsField id="jt" label="api_token" type="password" placeholder="••••••••••••••••" value={token} onChange={setToken} />
      <button type="submit" className={settingsPrimaryClass}>{stored ? 'Update' : 'Store'} Jira token</button>
      {msg && <SettingsStatus msg={msg} />}
    </form>
  )
}

function AdminForm({ stored, onDone }: { stored: boolean; onDone: (msg: string) => void }) {
  const [orgId, setOrgId] = useState('')
  const [key, setKey] = useState('')
  const [msg, setMsg] = useState<string | null>(null)
  async function submit(e: FormEvent) {
    e.preventDefault()
    setMsg(null)
    try {
      await api('/tokens', {
        method: 'POST',
        body: JSON.stringify({ token_type: 'org_admin_api_key', org_id: orgId, admin_key: key }),
      })
      setOrgId('')
      setKey('')
      onDone('Org-admin key stored.')
    } catch (err) {
      setMsg(`Error: ${(err as Error).message}`)
    }
  }
  return (
    <form onSubmit={submit} className="space-y-3">
      <SettingsField id="org" label="organization_id" placeholder="from the admin.atlassian.com org URL" value={orgId} onChange={setOrgId} />
      <SettingsField id="ak" label="admin_key" type="password" placeholder="••••••••••••••••" value={key} onChange={setKey} />
      <button type="submit" className={settingsPrimaryClass}>{stored ? 'Update' : 'Store'} org-admin key</button>
      {msg && <SettingsStatus msg={msg} />}
    </form>
  )
}

export default function SettingsTokens() {
  const [tokens, setTokens] = useState<TokenInfo[] | null>(null)
  const [msg, setMsg] = useState<string | null>(null)
  const [sel, setSel] = useSelection()

  const refresh = () => api<TokenInfo[]>('/tokens')
    .then((t) => setTokens(Array.isArray(t) ? t : []))
    .catch(() => setTokens([]))
  useEffect(() => {
    void refresh()
  }, [])

  const rows: Row[] = KINDS.map((k) => ({ ...k, stored: tokens?.find((t) => t.token_type === k.id) ?? null }))
  const selected = rows.find((r) => r.id === sel) ?? null

  async function remove(kind: Kind) {
    setMsg(null)
    try {
      await api(`/tokens/${kind}`, { method: 'DELETE' })
      setMsg('Credential removed.')
    } catch (err) {
      setMsg(`Error: ${(err as Error).message}`)
    }
    void refresh()
  }
  const done = (m: string) => {
    setMsg(m)
    void refresh()
  }

  return (
    <Page
      title="Settings"
      hint="Credentials are stored encrypted, per admin. Saved once — operations reuse them. Secrets are never shown back."
      context={<SettingsTabs />}
    >
      <FilterBar>
        <span className="font-mono text-[11px] text-text-3">
          {rows.filter((r) => r.stored).length} of {rows.length} stored · encrypted per admin · secrets are never shown back
        </span>
        {msg && <span className="ml-auto"><SettingsStatus msg={msg} /></span>}
      </FilterBar>
      <SplitView
        onCloseDetail={() => setSel(null)}
        emptyDetail="Select a credential to store, update or remove it."
        detail={selected && (
          <DetailPane
            eyebrow="Credential"
            title={selected.name}
            status={<StatusText status={selected.stored ? 'stored' : 'not stored'} />}
            actions={selected.stored && (
              <button type="button" className={detailDangerClass} onClick={() => void remove(selected.id)}>Remove</button>
            )}
          >
            <KeyValue
              items={[
                ['Used for', selected.usedFor],
                ['Label', <span className="font-mono">{selected.stored?.label ?? '—'}</span>],
                ['Stored', <span className="font-mono">{when(selected.stored?.created_at ?? null)}</span>],
                ['Last used', <span className="font-mono">{when(selected.stored?.last_used_at ?? null)}</span>],
              ]}
            />
            {selected.id === 'jira_api_token'
              ? <JiraForm key={selected.id} stored={!!selected.stored} onDone={done} />
              : <AdminForm key={selected.id} stored={!!selected.stored} onDone={done} />}
          </DetailPane>
        )}
        list={(
          <DataTable
            label="Credentials"
            rows={tokens === null ? [] : rows}
            columns={COLUMNS}
            rowId={(r) => r.id}
            selectedId={sel}
            onSelect={setSel}
            empty={tokens === null ? 'Loading…' : undefined}
          />
        )}
      />
    </Page>
  )
}
